import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';

    // Handle 401 (Unauthorized) - but NOT for auth/me endpoint (that's expected when not logged in)
    if (error.response?.status === 401 && !requestUrl.includes('/auth/me')) {
      // Only redirect for protected routes, not the initial auth check
      window.location.href = '/';
      return Promise.reject(error);
    }

    // Die Server-Antwort MUSS beim Aufrufer ankommen. Bis 03.08.2026 wurde hier ein
    // nackter `new Error(message)` geworfen und die Response weggeworfen — jede
    // Stelle, die `err.response.data.error` oder `err.response.status` liest, sah
    // deshalb nichts (Bugtracker #120: "Upload fehlgeschlagen" ohne Grund, und die
    // 409-Duplikat-Erkennung im Upload-Dialog lief komplett ins Leere).
    // `message` bleibt wie gehabt der Server-Grund — bestehende `err.message`-Leser
    // sind unberuehrt.
    const data = error.response?.data;
    const message = data?.error || data?.message || error.message || 'Ein Fehler ist aufgetreten';
    const wrapped: any = new Error(message);
    wrapped.response = error.response;
    wrapped.status = error.response?.status;
    wrapped.data = data;
    wrapped.code = data?.code || error.code;
    return Promise.reject(wrapped);
  }
);

// Auth endpoints
export const auth = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Dashboard
export const dashboard = {
  get: () => api.get('/dashboard'),
  getBauleiter: () => api.get('/dashboard/bauleiter'),
};

// Users
export const users = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id, reason) => api.delete(`/users/${id}`, { data: { reason } }),
  resetPassword: (id, password, createMattermost = false) => api.post(`/users/${id}/reset-password`, { password, create_mattermost: createMattermost }),
  setAppCredentials: (id, data) => api.post(`/users/${id}/app-credentials`, data),
  revokeAppAccess: (id) => api.post(`/users/${id}/revoke-app-access`),
  getYearlyStats: (year) => api.get('/users/yearly-stats', { params: { year } }),
  getYearOverview: (id, year) => api.get(`/users/${id}/year-overview`, { params: { year } }),
  getStammblatt: (id) => api.get(`/users/${id}/stammblatt`),
  updateStammblatt: (id, data) => api.put(`/users/${id}/stammblatt`, data),
};

// Time Entries
export const timeEntries = {
  getAll: (params) => api.get('/time-entries', { params }),
  getStats: (params) => api.get('/time-entries/stats', { params }),
  create: (data) => api.post('/time-entries', data),
  update: (id, data) => api.put(`/time-entries/${id}`, data),
  delete: (id, reason) => api.delete(`/time-entries/${id}`, { data: { reason } }),
  // GPS-based project assignment
  assignProjects: (data) => api.post('/time-entries/assign-projects', data),
  assignProject: (id) => api.post(`/time-entries/${id}/assign-project`),
};

// Sick Reports
export const sickReports = {
  getAll: (params) => api.get('/sick-reports', { params }),
  getStats: (params) => api.get('/sick-reports/stats', { params }),
  getById: (id) => api.get(`/sick-reports/${id}`),
  create: (data: { user_id: number; start_date: string; end_date?: string; notes?: string; proofPhoto?: File | null }) => {
    const fd = new FormData();
    fd.append('user_id', String(data.user_id));
    fd.append('start_date', data.start_date);
    if (data.end_date) fd.append('end_date', data.end_date);
    if (data.notes) fd.append('notes', data.notes);
    if (data.proofPhoto) fd.append('proofPhoto', data.proofPhoto);
    return api.post('/sick-reports', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  update: (id, data) => api.put(`/sick-reports/${id}`, data),
  delete: (id) => api.delete(`/sick-reports/${id}`),
};

// Vacation
export const vacation = {
  getAll: (params) => api.get('/vacation-requests', { params }),
  getCalendar: (params) => api.get('/vacation/calendar', { params }),
  getSummary: (params) => api.get('/vacation/summary', { params }),
  getStats: (params) => api.get('/vacation/stats', { params }),
  getById: (id) => api.get(`/vacation-requests/${id}`),
  create: (data) => api.post('/vacation-requests', data),
  update: (id, data) => api.put(`/vacation-requests/${id}`, data),
  approve: (id) => api.post(`/vacation-requests/${id}/approve`),
  reject: (id, reason) => api.post(`/vacation-requests/${id}/reject`, { reason }),
  delete: (id) => api.delete(`/vacation-requests/${id}`),
  sendReminder: () => api.post('/vacation/reminder'),
};

// Projects (Bauvorhaben)
export const projects = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id, force = false) => api.delete(`/projects/${id}`, { params: force ? { force: 'true' } : {} }),
  archive: (id) => api.post(`/projects/${id}/archive`),
  unarchive: (id) => api.post(`/projects/${id}/unarchive`),
  // Resolve Google Maps short URL to coordinates
  resolveMapsUrl: (url) => api.post('/projects/resolve-maps-url', { url }),
};

// Products
export const products = {
  getAll: (params) => api.get('/products', { params }),
  search: (q) => api.get('/products/search', { params: { q } }),
  getCategories: () => api.get('/products/categories'),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Orders
export const orders = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  update: (id, data) => api.put(`/orders/${id}`, data),
  complete: (id) => api.post(`/orders/${id}/complete`),
  reject: (id, reason) => api.post(`/orders/${id}/reject`, { reason }),
  remove: (id) => api.delete(`/orders/${id}`),
  sendQuestion: (id, itemId, question) => api.post(`/orders/${id}/question`, { item_id: itemId, question }),
  archive: (id) => api.post(`/orders/${id}/archive`),
  getHistory: (userId) => api.get(`/orders/history/${userId}`),
  // Bugtracker #98: Benachrichtigung neue Bestellungen
  getStats: () => api.get('/orders/stats'),
  markSeen: (id) => api.post(`/orders/${id}/mark-seen`),
  // Public endpoints (no auth required)
  getByToken: (token) => api.get(`/orders/by-token/${token}`),
  updateByToken: (token, data) => api.put(`/orders/by-token/${token}`, data),
  submitByToken: (token) => api.post(`/orders/by-token/${token}/submit`),
  // Public order history
  getOrderHistory: (userId) => api.get(`/orders/order-history/${userId}`),
  getExpiringSoon: (hours = 2) => api.get(`/orders/expiring-soon`, { params: { hours } }),
};

// Reports
export const reports = {
  daily: (params) => api.get('/reports/daily', { params }),
  monthly: (params) => api.get('/reports/monthly', { params }),
  vacationYearly: (params) => api.get('/reports/vacation-yearly', { params }),
  stats: () => api.get('/reports/stats'),
  projectStats: (projectId, params) => api.get(`/reports/projects/${projectId}`, { params }),
  // For PDF downloads (using blob for proper auth handling)
  downloadDailyPdf: (date) => api.get('/reports/daily', { params: { date, format: 'pdf' }, responseType: 'blob' }),
  downloadMonthlyPdf: (month, year) => api.get('/reports/monthly', { params: { month, year, format: 'pdf' }, responseType: 'blob' }),
  downloadVacationPdf: (year) => api.get('/reports/vacation-yearly', { params: { year, format: 'pdf' }, responseType: 'blob' }),
  // Legacy URL methods (kept for backwards compatibility, but may not work with auth)
  downloadDaily: (date) => `${api.defaults.baseURL}/reports/daily?date=${date}&format=pdf`,
  downloadMonthly: (month, year) => `${api.defaults.baseURL}/reports/monthly?month=${month}&year=${year}&format=pdf`,
  downloadVacation: (year) => `${api.defaults.baseURL}/reports/vacation-yearly?year=${year}&format=pdf`,
};

// Settings
export const settings = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  getAuditLog: (params) => api.get('/settings/audit-log', { params }),
  createBackup: () => api.post('/settings/backup'),
};

// Zähler (K.I. Feature)
export const zaehler = {
  getAll: (params) => api.get('/zaehler', { params }),
  getById: (id) => api.get(`/zaehler/${id}`),
  save: (data) => api.post('/zaehler', data),
  update: (id, data) => api.put(`/zaehler/${id}`, data),
  analyze: (image_base64) => api.post('/zaehler/analyze', { image_base64 }),
  // Delete operations
  delete: (id) => api.delete(`/zaehler/${id}`),
  deleteBatch: (ids) => api.delete('/zaehler/batch', { data: { ids } }),
  deleteBereich: (projectId, bereich) => api.delete(`/zaehler/bereich/${projectId}/${encodeURIComponent(bereich)}`),
  deleteProject: (projectId) => api.delete(`/zaehler/project/${projectId}`),
  // Google Drive integration
  getDriveStatus: () => api.get('/zaehler/drive-status'),
  uploadTemp: (image_base64, index, timestamp) => api.post('/zaehler/upload-temp', { image_base64, index, timestamp }),
  deleteTemp: (fileId) => api.delete(`/zaehler/temp/${fileId}`),
};

// Office Messages (Info ans Büro)
export const officeMessages = {
  getAll: (params) => api.get('/office-messages', { params }),
  getById: (id) => api.get(`/office-messages/${id}`),
  update: (id, data) => api.patch(`/office-messages/${id}`, data),
  delete: (id) => api.delete(`/office-messages/${id}`),
  getUnreadCount: () => api.get('/office-messages/count'),
  reply: (id, message, closeTicket = false) => api.post(`/office-messages/${id}/reply`, { message, closeTicket }),
  reopen: (id) => api.post(`/office-messages/${id}/reopen`),
};

// Construction Diary (Bautagebuch)
export const diary = {
  getAll: (params) => api.get('/diary', { params }),
  getProjects: () => api.get('/diary/projects'),
  getWeek: (projectId, year, week) => api.get(`/diary/week/${projectId}/${year}/${week}`),
  getEntry: (id) => api.get(`/diary/${id}`),
  updateEntry: (id, data) => api.patch(`/diary/${id}`, data),
  updateStatus: (id, status) => api.patch(`/diary/${id}/status`, { status }),
  getSignatures: () => api.get('/diary/signatures'),
};

// Defects (Mängelmeldungen)
export const defects = {
  getAll: (params) => api.get('/defects', { params }),
  getById: (id) => api.get(`/defects/${id}`),
  create: (data) => api.post('/defects', data),
  update: (id, data) => api.put(`/defects/${id}`, data),
  delete: (id) => api.delete(`/defects/${id}`),
  getStats: () => api.get('/defects/stats'),
  markRejectionsSeen: () => api.post('/defects/rejections/mark-seen'),
  // Status transitions
  schedule: (id, data) => api.post(`/defects/${id}/schedule`, data),
  assign: (id, data) => api.post(`/defects/${id}/assign`, data),
  accept: (id) => api.post(`/defects/${id}/accept`),
  reject: (id, data) => api.post(`/defects/${id}/reject`, data),
  start: (id) => api.post(`/defects/${id}/start`),
  complete: (id, data) => api.post(`/defects/${id}/complete`, data),
  skipSignature: (id) => api.post(`/defects/${id}/skip-signature`),
  refuseSignature: (id, data) => api.post(`/defects/${id}/refuse-signature`, data),
  needsFollowup: (id, data) => api.post(`/defects/${id}/needs-followup`, data),
  report: (id, data) => api.post(`/defects/${id}/report`, data),
  // Photos
  uploadPhoto: (id, formData) => api.post(`/defects/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deletePhoto: (id, photoId) => api.delete(`/defects/${id}/photos/${photoId}`),
  // Import
  analyzePdf: (formData) => api.post('/defects/analyze-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Email polling
  pollEmails: () => api.post('/defects/email-poll'),
  getEmailConfigs: () => api.get('/defects/email-config'),
  getEmailConfig: (id) => api.get(`/defects/email-config/${id}`),
  createEmailConfig: (data) => api.post('/defects/email-config', data),
  updateEmailConfig: (id, data) => api.put(`/defects/email-config/${id}`, data),
  deleteEmailConfig: (id) => api.delete(`/defects/email-config/${id}`),
  testEmailConnection: (id) => api.post(`/defects/email-config/${id}/test`),
};

// Stundenzettel (Timesheets)
export const stundenzettel = {
  getEntries: (params) => api.get('/stundenzettel', { params }),
  getEmployees: () => api.get('/stundenzettel/employees'),
  getOverview: (params) => api.get('/stundenzettel/overview', { params }),
  createEntry: (data) => api.post('/stundenzettel/entries', data),
  bulkUpdate: (data) => api.post('/stundenzettel/entries/bulk', data),
  deleteEntry: (id) => api.delete(`/stundenzettel/entries/${id}`),
  prefill: (data) => api.post('/stundenzettel/prefill', data),
  autofill: (data) => api.post('/stundenzettel/autofill', data),
  sync: (data) => api.post('/stundenzettel/sync', data),
  exportExcel: (params) => api.get('/stundenzettel/export/excel', { params, responseType: 'blob' }),
  exportPdf: (params) => api.get('/stundenzettel/export/pdf', { params, responseType: 'blob' }),
  exportPdfAll: (data) => api.post('/stundenzettel/export/pdf-all', data, { responseType: 'blob' }),
};

// GAEB Kalkulation
export const gaeb = {
  getProjects: () => api.get('/gaeb/projects'),
  createProject: (formData) => api.post('/gaeb/projects', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getProject: (id) => api.get(`/gaeb/projects/${id}`),
  updateProjectContext: (id, data) => api.put(`/gaeb/projects/${id}/context`, data),
  reparsePreambles: (id) => api.post(`/gaeb/projects/${id}/reparse-preambles`),
  deleteProject: (id) => api.delete(`/gaeb/projects/${id}`),
  getPositions: (id) => api.get(`/gaeb/projects/${id}/positions`),
  updatePosition: (projectId, posId, data) => api.put(`/gaeb/projects/${projectId}/positions/${posId}`, data),
  exportP94: (id) => api.post(`/gaeb/projects/${id}/export/p94`, {}, { responseType: 'blob' }),
  exportP93Rest: (id) => api.post(`/gaeb/projects/${id}/export/p93-rest`, {}, { responseType: 'blob' }),
  importP94: (id, formData) => api.post(`/gaeb/projects/${id}/import/p94`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDatanormStatus: () => api.get('/gaeb/datanorm/status'),
  searchDatanorm: (q, limit) => api.get('/gaeb/datanorm/search', { params: { q, limit } }),
  searchDatanormAdvanced: (q, mode = 'fts', limit = 15) => api.get('/gaeb/datanorm/search', { params: { q, mode, limit } }),
  resolveSingle: (projectId, posId, options) => api.post(`/gaeb/projects/${projectId}/resolve-single/${posId}`, options),
  // analyze + auto-resolve use SSE, handled directly in frontend via fetch
  analyzePositions: (projectId, options = {}) =>
    fetch(`/api/gaeb/projects/${projectId}/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
      credentials: 'include',
    }),
  // Composite items
  getCompositeItems: (posId) => api.get(`/gaeb/positions/${posId}/composite-items`),
  addCompositeItem: (posId, data) => api.post(`/gaeb/positions/${posId}/composite-items`, data),
  updateCompositeItem: (itemId, data) => api.put(`/gaeb/composite-items/${itemId}`, data),
  deleteCompositeItem: (itemId) => api.delete(`/gaeb/composite-items/${itemId}`),
  extractComposite: (projectId, posId, data) => api.post(`/gaeb/projects/${projectId}/positions/${posId}/extract-composite`, data),
  // Verify (Katalog-Übernahme)
  verifyPosition: (projectId, posId) => api.post(`/gaeb/projects/${projectId}/positions/${posId}/verify`),
  verifySingleItem: (projectId, posId, itemId) => api.post(`/gaeb/projects/${projectId}/positions/${posId}/verify-single/${itemId}`),
  // Mappings (Phase 3)
  getMappings: (params) => api.get('/gaeb/mappings', { params }),
  deleteMapping: (id) => api.delete(`/gaeb/mappings/${id}`),
  updateMapping: (id, data) => api.put(`/gaeb/mappings/${id}`, data),
  // Composite Templates (Phase C)
  getCompositeTemplates: (category) => api.get('/gaeb/composite-templates', { params: category ? { category } : {} }),
  createCompositeTemplate: (data) => api.post('/gaeb/composite-templates', data),
  deleteCompositeTemplate: (id) => api.delete(`/gaeb/composite-templates/${id}`),
  applyCompositeTemplate: (projectId, posId, templateId) => api.post(`/gaeb/projects/${projectId}/positions/${posId}/apply-template/${templateId}`),
  // Kalkulation v2: Projektprofil
  getProjectProfile: (projectId) => api.get(`/gaeb/projects/${projectId}/profile`),
  createProjectProfile: (projectId, options = {}) => api.post(`/gaeb/projects/${projectId}/create-profile`, options),
  getLohnzeiten: () => api.get('/gaeb/lohnzeiten'),
  getDefaults: () => api.get('/gaeb/defaults'),
  getWissenskatalog: (params) => api.get('/gaeb/wissenskatalog', { params }),
  // Kalkulation v2: Positions-Auflösung (SSE — use fetch, not axios)
  resolvePositionsUrl: (projectId) => `/api/gaeb/projects/${projectId}/resolve-positions`,
  // Kalkulation v2: Artikelsuche (SSE — use fetch, not axios)
  searchArticlesUrl: (projectId) => `/api/gaeb/projects/${projectId}/search-articles`,
  // Kalkulation v2: Auto-Resolve v2 (SSE — 5 Phasen: Profil → Auflösen → Suchen → OMD → Lücken)
  autoResolveV2Url: (projectId) => `/api/gaeb/projects/${projectId}/auto-resolve-v2`,
};

// Controlling (Phase 4)
export const controlling = {
  listBudgets: () => api.get('/controlling/budgets'),
  createFromGaeb: (data) => api.post('/controlling/create-from-gaeb', data),
  getDashboard: (budgetId) => api.get(`/controlling/budgets/${budgetId}/dashboard`),
  matchOrder: (budgetId, items) => api.post(`/controlling/budgets/${budgetId}/match-order`, { items }),
};

// Equipment (Geräte-/Maschinen-Tracking) — Backend: routes/equipment.js + services/equipmentService.js
export const equipment = {
  getAll: (params) => api.get('/equipment', { params }),
  getById: (id) => api.get(`/equipment/${id}`),
  getByCode: (code) => api.get(`/equipment/by-code/${encodeURIComponent(code)}`),
  getCategories: () => api.get('/equipment/categories'),
  getNextCode: () => api.get('/equipment/next-code'),
  getDguvAlerts: () => api.get('/equipment/dguv-alerts'),
  getStats: () => api.get('/equipment/stats'),
  getMeta: () => api.get('/equipment/meta'),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  move: (id, target) => api.post(`/equipment/${id}/move`, target),
  setCondition: (id, condition, note?) => api.post(`/equipment/${id}/condition`, { condition, note }),
  reportDefect: (id, description) => api.post(`/equipment/${id}/defects`, { description }),
  uploadFiles: (id, files, kind?, defectId?) => {
    const fd = new FormData()
    for (const f of files) fd.append('files', f)
    if (kind) fd.append('kind', kind)
    if (defectId) fd.append('defectId', String(defectId))
    return api.post(`/equipment/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  deletePhoto: (photoId) => api.delete(`/equipment/photos/${photoId}`),
  dguvCheck: (id, data) => api.post(`/equipment/${id}/dguv-check`, data),
  decommission: (id, note?) => api.post(`/equipment/${id}/decommission`, { note }),
  // Defektfälle
  getDefects: (scope?) => api.get('/equipment/defects', { params: { scope } }),
  decide: (defectId, decision, note?) => api.post(`/equipment/defects/${defectId}/decision`, { decision, note }),
  confirmDisposal: (defectId, note?) => api.post(`/equipment/defects/${defectId}/confirm-disposal`, { note }),
  rejectDisposal: (defectId, note?) => api.post(`/equipment/defects/${defectId}/reject-disposal`, { note }),
  closeDefect: (defectId, data) => api.post(`/equipment/defects/${defectId}/close`, data),
  // Orte
  getPlaces: (all?) => api.get('/equipment/places', { params: all ? { all: 1 } : {} }),
  createPlace: (data) => api.post('/equipment/places', data),
  updatePlace: (id, data) => api.put(`/equipment/places/${id}`, data),
  // Etiketten (Brother VC-500W): 1 Gerät → PNG, mehrere → ZIP
  downloadLabels: (ids, opts) => api.post('/equipment/labels', { ids, ...opts }, { responseType: 'blob' }),
  labelPreviewUrl: (id, opts) =>
    `${api.defaults.baseURL}/equipment/${id}/label.png?tapeMm=${opts.tapeMm}&symbology=${opts.symbology}&withName=${opts.withName ? 1 : 0}`,
};

// Invoices (Buchhaltung)
export const invoices = {
  getAll: (params) => api.get('/invoices', { params }),
  getStats: () => api.get('/invoices/stats'),
  getById: (id) => api.get(`/invoices/${id}`),
  upload: (formData) => api.post('/invoices/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  updateStatus: (id, status, note) => api.post(`/invoices/${id}/status`, { status, note }),
  setImportant: (id, important) => api.post(`/invoices/${id}/important`, { important }),
  pay: (id, amount) => api.post(`/invoices/${id}/pay`, { amount }),
  delete: (id) => api.delete(`/invoices/${id}`),
  findByNumbers: (numbers) => api.post('/invoices/find-by-numbers', { numbers }),
  getLinked: (id) => api.get(`/invoices/${id}/linked`),
  linkInvoices: (reminderId, invoiceIds) => api.post(`/invoices/${reminderId}/link`, { invoice_ids: invoiceIds }),
  unlinkInvoice: (reminderId, invoiceId) => api.delete(`/invoices/${reminderId}/link/${invoiceId}`),
  recalcFees: (id) => api.post(`/invoices/${id}/fees/recalc`),
  getSuppliers: () => api.get('/invoices/suppliers'),
  getCredits: (id) => api.get(`/invoices/${id}/credits`),
  applyCredits: (id, creditNoteIds) => api.post(`/invoices/${id}/apply-credits`, { creditNoteIds }),
  markInvoicesPaid: (reminderId, invoiceIds) => api.post(`/invoices/${reminderId}/mark-invoices-paid`, { invoiceIds }),
  // #130: Sammelüberweisung abschliessen — Betraege rechnet der Server.
  // paidAt (YYYY-MM-DD, optional): Tag des Zahlungseingangs, sonst "jetzt".
  markPaidBatch: (invoiceIds, applySkonto, paidAt?) => api.post('/invoices/mark-paid', { invoiceIds, applySkonto, paidAt }),
  // Ausgangsrechnungen: erst auslesen (ohne DB-Zeile), dann geprueft speichern
  getOutgoingStats: () => api.get('/invoices/outgoing/stats'),
  // 290 s: lange PDFs brauchen mehrere Minuten (36 Seiten ≈ 125 s), der NPM wartet 300 s
  analyzeOutgoing: (formData) => api.post('/invoices/outgoing/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 290000 }),
  createOutgoing: (data) => api.post('/invoices/outgoing', data),
  discardOutgoingUpload: (token) => api.delete(`/invoices/outgoing/upload/${encodeURIComponent(token)}`),
};

// Documents (Dokumentenablage)
export const documents = {
  getAll: (params) => api.get('/documents', { params }),
  getStats: () => api.get('/documents/stats'),
  getById: (id) => api.get(`/documents/${id}`),
  search: (q) => api.get('/documents/search', { params: { q } }),
  upload: (formData) => api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  chat: (message, sessionId) => api.post('/documents/chat', { message, sessionId }),
  compare: (id) => api.post(`/documents/${id}/compare`),
  getFileUrl: (id) => `/api/documents/${id}/file`,
  reanalyze: (id) => api.post(`/documents/${id}/reanalyze`),
  // Manuelle Uebernahme in die Buchhaltung (seit Juli 2026 kein Automatismus mehr)
  toInvoice: (id) => api.post(`/documents/${id}/to-invoice`),
  toInvoiceBulk: (ids) => api.post('/documents/to-invoice', { ids }),
};

// Fristen-Dokumentenablage (Backoffice)
export const fristen = {
  getCategories: () => api.get('/fristen/categories'),
  createCategory: (data) => api.post('/fristen/categories', data),
  renameCategory: (id, name) => api.put(`/fristen/categories/${id}`, { name }),
  deleteCategory: (id) => api.delete(`/fristen/categories/${id}`),
  upload: (categoryId, formData) => api.post(`/fristen/categories/${categoryId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getHistory: (categoryId) => api.get(`/fristen/categories/${categoryId}/history`),
  markRequested: (documentId, clear = false) => api.post(`/fristen/documents/${documentId}/requested`, { clear }),
  getDownloadUrl: (documentId) => `/api/fristen/documents/${documentId}/download`,
  downloadZip: (documentIds) => api.post('/fristen/download-zip', { document_ids: documentIds }, { responseType: 'blob' }),
  sendEmail: (data) => api.post('/fristen/send-email', data),
  getStats: () => api.get('/fristen/stats'),
  getSettings: () => api.get('/fristen/settings'),
  updateSettings: (data) => api.put('/fristen/settings', data),
};

export default api;
